import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, Loader2, RefreshCw, Server, Clock, AlertTriangle,
  CheckCircle2, XCircle, Minus,
} from "lucide-react";
import { syncApi } from "../../lib/api";
import { useInicio } from "../../lib/permisos";

// Umbrales: verde < 1h, ambar 1-3h, rojo > 3h
function estadoColor(ultimaSync: string | null): "green" | "amber" | "red" | "gray" {
  if (!ultimaSync) return "gray";
  const diff = Date.now() - new Date(ultimaSync).getTime();
  const horas = diff / (1000 * 60 * 60);
  if (horas < 1) return "green";
  if (horas < 3) return "amber";
  return "red";
}

const COLOR_CLASSES = {
  green:  "border-emerald-300 bg-emerald-50",
  amber:  "border-amber-300 bg-amber-50",
  red:    "border-rose-300 bg-rose-50",
  gray:   "border-slate-200 bg-slate-50",
};
const DOT_CLASSES = {
  green:  "bg-emerald-500",
  amber:  "bg-amber-500",
  red:    "bg-rose-500",
  gray:   "bg-slate-400",
};

function timeAgo(ts: string | null): string {
  if (!ts) return "Nunca";
  const diff = Date.now() - new Date(ts).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "Hace un momento";
  if (min < 60) return `Hace ${min} min`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `Hace ${hrs}h ${min % 60}m`;
  const dias = Math.floor(hrs / 24);
  return `Hace ${dias}d ${hrs % 24}h`;
}

function fmtFecha(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleString("es-VE", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

export default function SincronizacionPage() {
  const inicio = useInicio();
  const [logLimit, setLogLimit] = useState(50);

  const { data: resumen, isLoading: loadR, refetch: refetchR } = useQuery({
    queryKey: ["sync-resumen"],
    queryFn: () => syncApi.resumen(),
    refetchInterval: 60_000, // refresca cada minuto
  });

  const { data: logs, isLoading: loadL, refetch: refetchL } = useQuery({
    queryKey: ["sync-log", logLimit],
    queryFn: () => syncApi.logDetalle(logLimit),
    refetchInterval: 60_000,
  });

  function refetchAll() { refetchR(); refetchL(); }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={inicio} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
              <ArrowLeft size={16} /> Inicio
            </Link>
            <h1 className="text-xl font-bold text-slate-900">Sincronización</h1>
          </div>
          <button
            onClick={refetchAll}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-600 border border-slate-300 rounded-md hover:bg-slate-100"
          >
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>
      </header>

      <main className="flex-1 mx-auto max-w-7xl w-full px-6 py-6 space-y-6">

        {/* ── Tarjetas por sucursal ── */}
        <section>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
            Estado por sucursal
          </h2>
          {loadR ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <Loader2 className="animate-spin mr-2" size={20} /> Cargando…
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {(resumen ?? []).map((r: any) => {
                const color = estadoColor(r.ultimaSync);
                return (
                  <div
                    key={r.sucursalId}
                    className={`rounded-lg border p-4 ${COLOR_CLASSES[color]}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Server size={16} className="text-slate-500" />
                        <span className="font-semibold text-slate-900">{r.sucursalNombre}</span>
                      </div>
                      <span className={`w-2.5 h-2.5 rounded-full ${DOT_CLASSES[color]}`} />
                    </div>

                    <div className="space-y-1 text-sm">
                      <div className="flex items-center gap-1.5 text-slate-600">
                        <Clock size={13} />
                        <span>{timeAgo(r.ultimaSync)}</span>
                      </div>

                      {r.ultimaSync ? (
                        <>
                          <div className="text-xs text-slate-500">
                            {fmtFecha(r.ultimaSync)}
                          </div>
                          <div className="flex items-center gap-1.5 text-xs">
                            {r.estado === "ok" ? (
                              <CheckCircle2 size={13} className="text-emerald-600" />
                            ) : r.estado === "error" ? (
                              <XCircle size={13} className="text-rose-600" />
                            ) : (
                              <AlertTriangle size={13} className="text-amber-600" />
                            )}
                            <span className="text-slate-700">
                              {r.entidad}: {r.recibidos ?? 0} recibidos · {r.insertados ?? 0} ins · {r.actualizados ?? 0} act
                            </span>
                          </div>
                          {r.agenteVersion && (
                            <div className="text-xs text-slate-400">v{r.agenteVersion}</div>
                          )}
                        </>
                      ) : (
                        <div className="text-xs text-slate-400 flex items-center gap-1">
                          <Minus size={13} /> Sin datos de sincronización
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Log detallado ── */}
        <section className="bg-white rounded-lg border border-slate-200">
          <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
              Log de sincronización
            </h2>
            <select
              value={logLimit}
              onChange={(e) => setLogLimit(Number(e.target.value))}
              className="text-sm border border-slate-300 rounded px-2 py-1"
            >
              <option value={20}>Últimos 20</option>
              <option value={50}>Últimos 50</option>
              <option value={100}>Últimos 100</option>
              <option value={200}>Últimos 200</option>
            </select>
          </div>

          {loadL ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <Loader2 className="animate-spin mr-2" size={20} /> Cargando…
            </div>
          ) : !logs?.length ? (
            <div className="px-5 py-8 text-center text-sm text-slate-400">
              Sin registros de sincronización.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                    <th className="px-4 py-2 font-medium">Fecha</th>
                    <th className="px-4 py-2 font-medium">Sucursal</th>
                    <th className="px-4 py-2 font-medium">Entidad</th>
                    <th className="px-4 py-2 font-medium">Estado</th>
                    <th className="px-4 py-2 font-medium text-right">Recib.</th>
                    <th className="px-4 py-2 font-medium text-right">Ins.</th>
                    <th className="px-4 py-2 font-medium text-right">Act.</th>
                    <th className="px-4 py-2 font-medium text-right">ms</th>
                    <th className="px-4 py-2 font-medium">Versión</th>
                    <th className="px-4 py-2 font-medium">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l: any) => (
                    <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="px-4 py-2 text-xs text-slate-600 whitespace-nowrap">
                        {fmtFecha(l.finalizadaAt)}
                      </td>
                      <td className="px-4 py-2 text-slate-800">
                        {l.sucursalNombre}
                      </td>
                      <td className="px-4 py-2 text-slate-700">{l.entidad}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full border ${
                          l.estado === "ok"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : l.estado === "error"
                            ? "bg-rose-50 text-rose-700 border-rose-200"
                            : "bg-amber-50 text-amber-700 border-amber-200"
                        }`}>
                          {l.estado}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-slate-700">{l.recibidos}</td>
                      <td className="px-4 py-2 text-right text-slate-700">{l.insertados}</td>
                      <td className="px-4 py-2 text-right text-slate-700">{l.actualizados}</td>
                      <td className="px-4 py-2 text-right text-xs text-slate-500">{l.duracionMs ?? "—"}</td>
                      <td className="px-4 py-2 text-xs text-slate-400">{l.agenteVersion ?? "—"}</td>
                      <td className="px-4 py-2 text-xs text-rose-600 max-w-xs truncate" title={l.errorMensaje ?? ""}>
                        {l.errorMensaje ? l.errorMensaje.slice(0, 60) + (l.errorMensaje.length > 60 ? "…" : "") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

      </main>
    </div>
  );
}
